/**
 * Dashboard charts. Donut is hand-rolled SVG; the onboarding trend uses Recharts
 * (themed to the maroon palette) for a real labeled, interactive area chart.
 */
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CHART_HEX, BRAND_HEX } from '../lib/tokens';

export interface Segment {
  label: string;
  value: number;
  color: string;
}

export function Donut({
  segments,
  size = 168,
  thickness = 24,
}: {
  segments: Segment[];
  size?: number;
  thickness?: number;
}) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const r = (size - thickness) / 2;
  const circ = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={CHART_HEX.track} strokeWidth={thickness} />
      <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
        {segments.map((s, i) => {
          const len = (s.value / total) * circ;
          const el = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={thickness}
              strokeDasharray={`${len} ${circ - len}`}
              strokeDashoffset={-acc}
              strokeLinecap="butt"
            />
          );
          acc += len;
          return el;
        })}
      </g>
      <text
        x="50%"
        y="47%"
        textAnchor="middle"
        dominantBaseline="middle"
        style={{ fill: CHART_HEX.ink, fontFamily: 'Fraunces, serif', fontSize: size * 0.27, fontWeight: 600 }}
      >
        {total}
      </text>
      <text
        x="50%"
        y="62%"
        textAnchor="middle"
        dominantBaseline="middle"
        style={{ fill: CHART_HEX.inkFaint, fontFamily: 'IBM Plex Mono, monospace', fontSize: size * 0.072 }}
      >
        clientes
      </text>
    </svg>
  );
}

interface TrendDatum {
  label: string;
  value: number;
}

function TrendTooltip({ active, payload }: { active?: boolean; payload?: { payload: TrendDatum }[] }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]!.payload;
  return (
    <div className="rounded-xl border border-line bg-panel px-3 py-2 shadow-pop">
      <div className="font-mono text-[10px] uppercase tracking-wide text-ink-faint">{d.label}</div>
      <div className="text-sm font-bold text-ink">
        {d.value} {d.value === 1 ? 'cliente' : 'clientes'}
      </div>
    </div>
  );
}

/** Onboarding trend — themed Recharts area chart with week axis + hover tooltip. */
export function TrendArea({
  points,
  height = 180,
  color = BRAND_HEX.primary,
  labels,
}: {
  points: number[];
  height?: number;
  color?: string;
  labels?: string[];
}) {
  const data: TrendDatum[] = points.map((value, i) => ({
    label: labels?.[i] ?? `Sem ${i + 1}`,
    value,
  }));

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
          <defs>
            <linearGradient id="onboardingFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.28} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke={CHART_HEX.grid} strokeDasharray="3 3" />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tick={{ fill: CHART_HEX.inkFaint, fontSize: 11, fontFamily: 'IBM Plex Mono, monospace' }}
            dy={6}
          />
          <YAxis
            width={36}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            tick={{ fill: CHART_HEX.inkFaint, fontSize: 11, fontFamily: 'IBM Plex Mono, monospace' }}
          />
          <Tooltip
            content={<TrendTooltip />}
            cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: '4 4', strokeOpacity: 0.5 }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2.5}
            fill="url(#onboardingFill)"
            dot={{ r: 3, fill: CHART_HEX.panel, stroke: color, strokeWidth: 2 }}
            activeDot={{ r: 5, fill: color, stroke: CHART_HEX.panel, strokeWidth: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
